// SF2 — Cancellation Rate Calculator.
//
// One rate over the window and the products behind it. Nothing else: who
// cancelled and why is SF3, and the threshold this should be judged against is
// SF6. Mixing them here would make the number harder to read, not easier.
//
// The window selects when orders were PLACED, not when they were cancelled. An
// order placed before the window and cancelled inside it is not counted, which
// is the one case where the defensible denominators genuinely disagree, so the
// surface says it rather than leaving it to be discovered.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiClock, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderFulfillment.module.css';
import { getCancellations } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

const percent = (ratio, digits = 2) =>
  ratio === null || ratio === undefined ? <Blank /> : `${(ratio * 100).toFixed(digits)}%`;

// A rising cancellation rate is bad, so the colours are inverted against every
// other trend on the site. Up is red here.
const Trend = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const worse = value > 0;
  return (
    <span className={worse ? local.down : local.up}>
      {worse ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

const CancellationRate = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getCancellations(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rate = data?.rate;
  const products = data?.products || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Cancellation rate</h2>
        {rate && <span className={local.muted}>{count(rate.orders)} orders placed</span>}
      </div>
      <p className={local.muted}>
        Every cancellation, whoever made it. The window selects when orders were
        placed, so an order placed earlier and cancelled inside it is not counted.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load cancellations'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && rate && (
        <>
          <div className={local.todayRow}>
            <div className={local.todayTile}>
              <div className={local.todayLabel}>Cancellation rate</div>
              <div className={local.todayValue}>{percent(rate.rate)}</div>
              <div className={local.todayFoot}>
                {count(rate.cancelled)} of {count(rate.orders)} orders
                {rate.replacements_excluded > 0 && (
                  <>
                    {' · '}{count(rate.replacements_excluded)} replacement
                    {rate.replacements_excluded === 1 ? '' : 's'} left out
                  </>
                )}
              </div>
            </div>
            <div className={local.todayTile}>
              <div className={local.todayLabel}>Against the prior period</div>
              <div className={local.todayValue}><Trend value={data.trend} /></div>
              <div className={local.todayFoot}>
                {data.comparison?.available
                  ? `was ${percent(data.comparison.rate)}`
                  : 'not enough history to compare'}
              </div>
            </div>
            {/* Recent cohorts keep accruing cancellations, so the headline and
                the settled figure are shown side by side rather than one being
                quietly revised upward later. */}
            {rate.maturing && (
              <div className={local.todayTile}>
                <div className={local.todayLabel}>
                  <FiClock /> Settled orders only
                </div>
                <div className={local.todayValue}>{percent(rate.settled_rate)}</div>
                <div className={local.todayFoot}>
                  excludes {count(rate.maturing_orders)} orders from the last{' '}
                  {data.maturing_days} days, which can still be cancelled
                </div>
              </div>
            )}
          </div>

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Products driving it
              <span className={local.groupCount}>
                {count(products.length)} ranked of {count(data.product_count)}
              </span>
            </h3>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th className={local.numeric}>Orders</th>
                  <th className={local.numeric}>Cancelled</th>
                  <th className={local.numeric}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={4}>
                      No product reached {data.min_orders_for_ranking} orders in this period.
                    </td>
                  </tr>
                ) : products.map((row) => (
                  <tr key={row.asin}>
                    <td className={local.drillId}>{row.asin}</td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                    <td className={local.numeric}>{count(row.cancelled)}</td>
                    <td className={local.numeric}>
                      <span className={row.rate >= 0.2 ? local.down : undefined}>
                        {percent(row.rate, 1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CancellationRate;
